import React, { useState } from "react";
import logo from "../assets/logo.png";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField,
  Typography,
  Link,
  InputAdornment,
  Alert,
  AlertTitle,
  Button,
  Stack,
} from "@mui/material";
import { institutions, states } from "./statesAndInstitutions";
import { nursingSpecialties } from "./specialties";
import { LoadingButton } from "@mui/lab";
import { httpService } from "../httpService";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { User } from "lucide-react";
import { Badge, Modal } from "react-bootstrap";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

function CreateAccount() {
  const [account, setAccount] = useState({});
  const [errorPassword, setErrorPassword] = useState(false);

  const [errorLicense, setErrorLicense] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [phoneHelperText, setPhoneHelperText] = useState("");

  const [
    specialtiesAndRegistrationNumbers,
    setSpecialtiesAndRegistrationNumbers,
  ] = useState([]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const validatePhone = (value) => {
    const phoneRegex = /^0\d{10}$/; // Starts with 0 and has 11 digits total
    return phoneRegex.test(value);
  };

  const handleBlur = (e) => {
    if (e.target.name === "emailAddress") {
      if (!account.emailAddress) {
        setError(true);
        setHelperText("Email is required");
      }
    }

    if (e.target.name === "phoneNumber") {
      if (!account.phoneNumber) {
        setError(true);
        setHelperText("Phone number is required");
      }
    }
  };

  const handleChange = (e) => {
    // setAccount({ ...account, [e.target.name]: e.target.value });

    if (e.target.name === "emailAddress") {
      const value = e.target.value;

      if (!value) {
        setError(true);
        setHelperText("Email is required");
      } else if (!validateEmail(value)) {
        setError(true);
        setHelperText("Enter a valid email address");
      } else {
        setError(false);
        setHelperText("");
      }

      setAccount({
        ...account,
        [e.target.name]: e.target.value,
      });
      return;
    }

    if (e.target.name === "phoneNumber") {
      const value = e.target.value;

      // Only allow numeric input
      if (!/^\d*$/.test(value)) return;

      if (!value) {
        setPhoneError(true);
        setPhoneHelperText("Phone number is required");
      } else if (!validatePhone(value)) {
        setPhoneError(true);
        setPhoneHelperText("Enter a valid 11-digit Nigerian phone number");
      } else {
        setPhoneError(false);
        setPhoneHelperText("");
      }

      setAccount({
        ...account,
        [e.target.name]: e.target.value,
      });
      return;
    }

    if (e.target.name === "residence" && e.target.value === "school") {
      setAccount({ ...account, [e.target.name]: e.target.value, address: "" });
    } else if (
      e.target.name === "residence" &&
      e.target.value === "clinical area"
    ) {
      setAccount({
        ...account,
        [e.target.name]: e.target.value,
        institution: "",
      });
    } else {
      setAccount({
        ...account,
        [e.target.name]: e.target.value,
      });
    }
  };

  const selectSpecialty = (e) => {
    if (e.target.checked) {
      setSelectedSpecialties([...selectedSpecialties, e.target.value]);
    } else {
      setSelectedSpecialties(
        selectedSpecialties.filter((item) => item !== e.target.value)
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const updateOrAddSpecialtyAndRegistrationNumber = (
    specialty,
    registrationNumber
  ) => {
    setSpecialtiesAndRegistrationNumbers((prevUsers) => {
      const userIndex = prevUsers.findIndex(
        (user) => user.specialty === specialty
      );
      if (userIndex !== -1) {
        // update existing user
        return prevUsers.map((user) =>
          user.specialty === specialty ? { ...user, registrationNumber } : user
        );
      } else {
        // add new user
        return [...prevUsers, { specialty, registrationNumber }];
      }
    });
  };
  const createAccount = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "question",
      title: "Create Account",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);

        const body = {
          ...account,
          specialties: selectedSpecialties,
          specialtiesAndRegistrationNumbers,
        };
        const { data, error } = await httpService.post("register", body);

        if (data) {
          window.location.assign("/accountcreated");
        }

        if (error) {
          toast.error(error);
        }
        setLoading(false);
      }
    });
  };
  return (
    <div>
      <div className="container mt-5">
        <div className="">
          <div className="text-center">
            <img src={logo} alt="logo" height={100} className="mb-4" />

            <div className="text-center mb-4">
              <Typography variant="h6">CREATE YOUR EXAMINER ACCOUNT</Typography>
            </div>
          </div>
          <form className="mb-4" onSubmit={handleSubmit}>
            <div className="row d-flex justify-content-center mb-3">
              <div className="col-lg-6 mb-5">
                <div className="mb-3">
                  <Typography variant="caption">Names</Typography>
                  <Alert severity="info">
                    Please enter your name as displayed on your license card
                  </Alert>
                </div>
                <div className="mb-3">
                  <TextField
                    label="First Name"
                    name="firstName"
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    label="Last Name"
                    name="lastName"
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    label="Other Name"
                    name="otherName"
                    onChange={handleChange}
                    fullWidth
                  />
                </div>
              </div>
              <div className="col-lg-6 mb-5">
                <div className="mb-3">
                  <Typography variant="caption">Location </Typography>
                  <Alert severity="info">
                    Please fill accurately your location of practice
                  </Alert>
                </div>
                <div className="mb-3">
                  <TextField
                    label="State of practice"
                    name="state"
                    onChange={handleChange}
                    fullWidth
                    select
                    required
                  >
                    {states.sort().map((c, i) => (
                      <MenuItem key={i} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="mb-3">
                  <TextField
                    label="Area of practice"
                    name="residence"
                    onChange={handleChange}
                    fullWidth
                    select
                    required
                  >
                    <MenuItem value="school">Institution</MenuItem>
                    <MenuItem value="clinical area">Clinical Area</MenuItem>
                  </TextField>
                </div>
                {account.residence === "school" ? (
                  <div className="mb-3">
                    <TextField
                      required
                      label="Institution"
                      name="institution"
                      fullWidth
                      select
                      id="filled-select-currency-native"
                      onChange={handleChange}
                    >
                      {institutions.find((c) => c.state === account.state) &&
                        institutions
                          .find((c) => c.state === account.state)
                          .schools.sort()
                          .map((c, i) => (
                            <MenuItem key={i} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                    </TextField>
                  </div>
                ) : (
                  <div className="mb-3">
                    <TextField
                      label="Address"
                      fullWidth
                      required
                      name="address"
                      onChange={handleChange}
                      multiline={true}
                      maxRows={4}
                    />
                  </div>
                )}
              </div>

              <div
                className="col-lg-6 mb-3 rounded overflow-scroll  bg-light  p-4"
                style={{ height: "30vh" }}
              >
                <Typography gutterBottom color="GrayText">
                  Select your specialties
                </Typography>
                <Alert severity="info">
                  Select all your specialties. You will be asked to provide
                  registration numbers for each specialty
                </Alert>
                <FormGroup>
                  {nursingSpecialties.map((c, i) => (
                    <FormControlLabel
                      key={i}
                      control={<Checkbox />}
                      label={c.specialty}
                      value={c.specialty}
                      onChange={selectSpecialty}
                    />
                  ))}
                </FormGroup>
              </div>

              <div className="col-lg-6 mb-5">
                <div className="mb-3">
                  <Typography variant="caption">License Number</Typography>
                  <Alert severity="warning">
                    Please enter your license number and ensure it is correct.
                  </Alert>
                </div>
                <div className="mb-3">
                  <TextField
                    label="License Number"
                    fullWidth
                    name="licenseNumber"
                    error={errorLicense}
                    required
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    label="Confirm License Number"
                    fullWidth
                    helperText={errorLicense && "License numbers don't match"}
                    required
                    error={errorLicense}
                    onBlur={(e) =>
                      setErrorLicense(e.target.value !== account.licenseNumber)
                    }
                    //  onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-lg-6 mb-5">
                <div className="mb-3">
                  <Typography variant="caption">
                    Email and Phone Number
                  </Typography>
                  <Alert severity="warning">
                    Please enter your email address and phone number. Ensure
                    they are unique to you and you alone.
                  </Alert>
                </div>
                <div className="mb-3">
                  <TextField
                    label="Phone Number"
                    name="phoneNumber"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={phoneError}
                    helperText={phoneHelperText}
                    fullWidth
                    required
                    type="tel"
                  />
                </div>

                <div className="mb-3">
                  <TextField
                    label="Email Address"
                    name="emailAddress"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    required
                    error={error}
                    helperText={helperText}
                    autoComplete=""
                  />
                </div>
              </div>

              <div className="col-lg-6 mb-5">
                <div className="mb-3">
                  <Typography variant="caption">Passwords</Typography>
                  <Alert severity="warning">
                    Please enter a password you are sure to remember.
                  </Alert>
                </div>
                <div className="mb-3">
                  <TextField
                    label="Password"
                    name="password"
                    onChange={handleChange}
                    fullWidth
                    type="password"
                    required
                    error={errorPassword}
                  />
                </div>
                <div className="mb-3">
                  <TextField
                    label="Confirm Password"
                    name="confirmPassword"
                    error={errorPassword}
                    helperText={errorPassword && "Passwords do not match"}
                    onBlur={(e) =>
                      setErrorPassword(e.target.value !== account.password)
                    }
                    fullWidth
                    required
                    type="password"
                  />
                </div>
              </div>
              <div className="">
                <div className="mb-3">
                  <LoadingButton
                    loading={loading}
                    endIcon={<KeyboardArrowRight />}
                    type="submit"
                    disabled={
                      errorPassword || errorLicense || error || phoneError
                    }
                    color="success"
                    variant="contained"
                    loadingPosition="end"
                  >
                    Proceed
                  </LoadingButton>
                </div>

                <Link href="/">Already have an account? Login</Link>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Modal
        onHide={() => {
          setShowModal(false);
          setSpecialtiesAndRegistrationNumbers([]);
        }}
        show={showModal}
        centered
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Typography variant="h5" fontWeight={500}>
              INPUT REGISTRATION NUMBER
            </Typography>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ height: "60vh", overflowY: "scroll" }}>
            <div className="row m-0 pt-4 pb-4">
              <div className="col-lg-6">
                {selectedSpecialties.map((c, i) => (
                  <div key={i} className="mb-5">
                    <TextField
                      fullWidth
                      type="number"
                      label="Enter Registration Number"
                      helperText={c.toUpperCase()}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          setSpecialtiesAndRegistrationNumbers((data) =>
                            data.filter((d) => d.specialty !== c)
                          );
                          return;
                        }

                        updateOrAddSpecialtyAndRegistrationNumber(
                          c,
                          `${
                            nursingSpecialties.find((d) => d.specialty === c)
                              .abbreviation + e.target.value
                          }`
                        );
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              {
                                nursingSpecialties.find(
                                  (d) => d.specialty === c
                                ).abbreviation
                              }
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="col-lg-6 border-start">
                <div className="mb-3">
                  <Alert severity="warning">
                    <AlertTitle>PLEASE CONFIRM</AlertTitle>
                    Please confirm your registration numbers for all your
                    selected specialties before proceeding
                  </Alert>
                </div>
                {specialtiesAndRegistrationNumbers.map((c, i) => (
                  <div
                    className={`mb-3 p-3  ${
                      i % 2 === 0 ? "bg-light" : "bg-white"
                    }`}
                  >
                    <Stack direction={"row"} spacing={2}>
                      <div className="d-flex align-items-center">
                        <Typography variant="h6">{i + 1}.</Typography>
                      </div>
                      <div>
                        <Typography
                          gutterBottom
                          textTransform={"uppercase"}
                          fontWeight={400}
                          color="GrayText"
                          sx={{ fontSize: 14 }}
                        >
                          {c.specialty}
                        </Typography>
                        <Typography
                          color="GrayText"
                          fontSize={20}
                          fontWeight={700}
                        >
                          {c.registrationNumber}
                        </Typography>
                      </div>
                    </Stack>
                  </div>
                ))}
                <Badge>
                  {specialtiesAndRegistrationNumbers.length} /{" "}
                  {selectedSpecialties.length} registration numbers
                </Badge>
              </div>
            </div>
          </div>
        </Modal.Body>
        <form onSubmit={createAccount}>
          <Modal.Footer>
            <FormGroup>
              <FormControlLabel
                required
                control={<Checkbox />}
                label="I confirm that these registration numbers are correct"
              />
            </FormGroup>
            <Button
              startIcon={<KeyboardArrowLeft />}
              className="me-2"
              onClick={() => {
                setShowModal(false);
                setSpecialtiesAndRegistrationNumbers([]);
              }}
            >
              Back to registration
            </Button>
            <LoadingButton
              variant="contained"
              disabled={
                specialtiesAndRegistrationNumbers.length !==
                  selectedSpecialties.length ||
                error ||
                errorPassword
              }
              type="submit"
              loading={loading}
            >
              Create Account
            </LoadingButton>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

export default CreateAccount;
